import React from 'react';

const ResumeEditor = ({ resumeData, setResumeData }) => {
  const handleChange = (e, section, index, field) => {
    if (section === 'personalInfo') {
      setResumeData({ ...resumeData, personalInfo: { ...resumeData.personalInfo, [field]: e.target.value } });
    } else if (section === 'skills') {
      setResumeData({ ...resumeData, skills: e.target.value.split(',').map(skill => skill.trim()) });
    } else {
      const updatedSection = [...resumeData[section]];
      updatedSection[index][field] = e.target.value;
      setResumeData({ ...resumeData, [section]: updatedSection });
    }
  };

  const addItem = (section, template) => {
    setResumeData({ ...resumeData, [section]: [...resumeData[section], template] });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setResumeData({ ...resumeData, personalInfo: { ...resumeData.personalInfo, profileImage: e.target.result } });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setResumeData({ ...resumeData, personalInfo: { ...resumeData.personalInfo, profileImage: null } });
  };

  return (
    <div className="p-6 bg-white shadow-lg rounded-lg border border-gray-200 max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">Edit Your Resume</h2>
      
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
        <h3 className="font-semibold text-gray-800 mb-2">Resume Building Tips:</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Keep your summary concise (2-3 sentences) and highlight your key strengths</li>
          <li>• Use action verbs in experience descriptions (e.g., "Developed", "Improved", "Managed")</li>
          <li>• Quantify achievements where possible (e.g., "Increased sales by 25%")</li>
          <li>• Tailor your resume for each job application</li>
          <li>• Include relevant projects to showcase your skills</li>
        </ul>
      </div>
      
      {/* Personal Info */}
      <div className="mb-8 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-xl font-semibold mb-4 text-blue-800">Personal Information</h3>
        
        {/* Profile Image */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">Professional Photo (Optional)</label>
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
              id="profile-image"
            />
            <label
              htmlFor="profile-image"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer transition-colors duration-200 shadow-sm"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Choose Image
            </label>
            {resumeData.personalInfo.profileImage && (
              <button
                onClick={removeImage}
                className="inline-flex items-center px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Remove
              </button>
            )}
          </div>
          {resumeData.personalInfo.profileImage && (
            <div className="mt-2">
              <img
                src={resumeData.personalInfo.profileImage}
                alt="Profile Preview"
                className="w-20 h-20 object-cover border border-gray-300"
              />
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input 
            className="border p-3 rounded-lg focus:ring-2 focus:ring-blue-500" 
            placeholder="Full Name" 
            value={resumeData.personalInfo.fullName}
            onChange={(e) => handleChange(e, 'personalInfo', null, 'fullName')} 
          />
          <input 
            className="border p-3 rounded-lg focus:ring-2 focus:ring-blue-500" 
            placeholder="Email" 
            value={resumeData.personalInfo.email}
            onChange={(e) => handleChange(e, 'personalInfo', null, 'email')} 
          />
          <input 
            className="border p-3 rounded-lg focus:ring-2 focus:ring-blue-500" 
            placeholder="Phone" 
            value={resumeData.personalInfo.phone}
            onChange={(e) => handleChange(e, 'personalInfo', null, 'phone')} 
          />
          <input 
            className="border p-3 rounded-lg focus:ring-2 focus:ring-blue-500" 
            placeholder="Address" 
            value={resumeData.personalInfo.address}
            onChange={(e) => handleChange(e, 'personalInfo', null, 'address')} 
          />
        </div>
        <textarea 
          className="w-full mt-4 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500" 
          placeholder="Professional Summary: Write 2-3 sentences about your career goals, key skills, and what you bring to the table. Example: 'Experienced software developer with 5+ years in full-stack development, specializing in React and Node.js. Passionate about creating user-friendly applications and solving complex problems. Seeking opportunities to contribute to innovative tech projects.'" 
          rows="3"
          value={resumeData.personalInfo.summary}
          onChange={(e) => handleChange(e, 'personalInfo', null, 'summary')} 
        />
      </div>

      {/* Education */}
      <div className="mb-8 p-4 bg-green-50 rounded-lg">
        <h3 className="text-xl font-semibold mb-4 text-green-800">Education</h3>
        {resumeData.education.map((edu, index) => (
          <div key={index} className="mb-4 p-4 border rounded bg-white">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input 
                className="border p-2 rounded" 
                placeholder="School/University" 
                value={edu.school}
                onChange={(e) => handleChange(e, 'education', index, 'school')} 
              />
              <input 
                className="border p-2 rounded" 
                placeholder="Degree" 
                value={edu.degree}
                onChange={(e) => handleChange(e, 'education', index, 'degree')} 
              />
              <input 
                className="border p-2 rounded" 
                placeholder="Year" 
                value={edu.year}
                onChange={(e) => handleChange(e, 'education', index, 'year')} 
              />
            </div>
            <button 
              onClick={() => removeItem('education', index)}
              className="mt-2 text-red-600 hover:text-red-800"
            >
              Remove
            </button>
          </div>
        ))}
        <button 
          onClick={() => addItem('education', { school: '', degree: '', year: '' })}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          + Add Education
        </button>
      </div>

      {/* Work Experience */}
      <div className="mb-8 p-4 bg-purple-50 rounded-lg">
        <h3 className="text-xl font-semibold mb-4 text-purple-800">Work Experience</h3>
        {resumeData.experience.map((exp, index) => (
          <div key={index} className="mb-4 p-4 border rounded bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
              <input 
                className="border p-2 rounded" 
                placeholder="Company" 
                value={exp.company}
                onChange={(e) => handleChange(e, 'experience', index, 'company')} 
              />
              <input 
                className="border p-2 rounded" 
                placeholder="Role/Position" 
                value={exp.role}
                onChange={(e) => handleChange(e, 'experience', index, 'role')} 
              />
            </div>
            <input 
              className="w-full mb-2 p-2 border rounded" 
              placeholder="Duration (e.g., Jan 2020 - Dec 2022)" 
              value={exp.duration}
              onChange={(e) => handleChange(e, 'experience', index, 'duration')} 
            />
            <textarea 
              className="w-full p-2 border rounded" 
              placeholder="Description: Use bullet points to describe your key responsibilities, achievements, and impact. Example: • Developed and maintained web applications using React and Node.js • Improved application performance by 40% through code optimization • Collaborated with cross-functional teams to deliver projects on time" 
              rows="3"
              value={exp.description}
              onChange={(e) => handleChange(e, 'experience', index, 'description')} 
            />
            <button 
              onClick={() => removeItem('experience', index)}
              className="mt-2 text-red-600 hover:text-red-800"
            >
              Remove
            </button>
          </div>
        ))}
        <button 
          onClick={() => addItem('experience', { company: '', role: '', duration: '', description: '' })}
          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
        >
          + Add Experience
        </button>
      </div>

      {/* Skills */}
      <div className="mb-8 p-4 bg-yellow-50 rounded-lg">
        <h3 className="text-xl font-semibold mb-4 text-yellow-800">Skills</h3>
        <textarea 
          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-yellow-500" 
          placeholder="Enter skills separated by commas (e.g., JavaScript, React, Node.js, Python)" 
          rows="3"
          value={resumeData.skills.join(', ')}
          onChange={(e) => handleChange(e, 'skills', null, null)} 
        />
      </div>

      {/* Projects */}
      <div className="mb-8 p-4 bg-red-50 rounded-lg">
        <h3 className="text-xl font-semibold mb-4 text-red-800">Projects</h3>
        {resumeData.projects.map((proj, index) => (
          <div key={index} className="mb-4 p-4 border rounded bg-white">
            <input 
              className="w-full mb-2 p-2 border rounded" 
              placeholder="Project Title" 
              value={proj.title}
              onChange={(e) => handleChange(e, 'projects', index, 'title')} 
            />
            <input 
              className="w-full mb-2 p-2 border rounded" 
              placeholder="Project Link (optional)" 
              value={proj.link}
              onChange={(e) => handleChange(e, 'projects', index, 'link')} 
            />
            <textarea 
              className="w-full p-2 border rounded" 
              placeholder="Project Description" 
              rows="3"
              value={proj.description}
              onChange={(e) => handleChange(e, 'projects', index, 'description')} 
            />
            <button 
              onClick={() => removeItem('projects', index)}
              className="mt-2 text-red-600 hover:text-red-800"
            >
              Remove
            </button>
          </div>
        ))}
        <button 
          onClick={() => addItem('projects', { title: '', link: '', description: '' })}
          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          + Add Project
        </button>
      </div>
    </div>
  );
};

export default ResumeEditor;